import { useEffect } from 'react';
import { restoreSessionDocuments, type ChatDocument, useAppDispatch } from '@/store';
import type { MessageAttachedDocument } from '@/types/chat';
import { FileStatus } from '@/types/file';

type DeferredSendSnapshot = {
  documentIds: string[];
  messageAttachmentDocuments?: MessageAttachedDocument[];
};

type PendingMessageSnapshot = {
  attachedDocuments?: MessageAttachedDocument[];
};

export function useRestoreSessionDocumentsForDeferredSend({
  deferredSendFromStore,
  pendingMessage,
  sessionDocuments,
  chatDocuments,
  isWaitingForDocumentProcessing,
}: {
  deferredSendFromStore: DeferredSendSnapshot | undefined;
  pendingMessage: PendingMessageSnapshot | undefined;
  sessionDocuments: ChatDocument[];
  chatDocuments: ChatDocument[];
  isWaitingForDocumentProcessing: boolean;
}) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isWaitingForDocumentProcessing) return;
    if (!deferredSendFromStore?.documentIds.length) return;

    const deferredIdSet = new Set(deferredSendFromStore.documentIds);
    const existingSessionIds = new Set(sessionDocuments.map((d) => d.documentId));
    const promotedDocumentIds = new Set(chatDocuments.map((d) => d.documentId));

    const attachmentSource = pendingMessage?.attachedDocuments?.length
      ? pendingMessage.attachedDocuments
      : (deferredSendFromStore.messageAttachmentDocuments ?? []);

    const toRestore = attachmentSource.filter(
      (doc) =>
        deferredIdSet.has(doc.documentId) &&
        !existingSessionIds.has(doc.documentId) &&
        !promotedDocumentIds.has(doc.documentId),
    );

    if (toRestore.length === 0) return;

    dispatch(
      restoreSessionDocuments(
        toRestore.map((doc) => ({
          documentId: doc.documentId,
          documentName: doc.documentName,
          status: FileStatus.CONTENT_EXTRACTION_IN_PROGRESS,
          isPromptAttachment: true,
        })),
      ),
    );
  }, [
    chatDocuments,
    deferredSendFromStore,
    dispatch,
    isWaitingForDocumentProcessing,
    pendingMessage,
    sessionDocuments,
  ]);
}
